// NodeDateRangeInput.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { addDays, isCompleteRawDate } from 'node/form/fields/utilities/dateFieldUtils';
import { normalizeDateValue, timestampFromDate } from 'utilities/DateTime';
import { clearDates } from 'utilities/Events';

// CSS
import './NodeDateRangeInput.css';

// Components
import DatePicker from 'components/datepicker/DatePicker';

/**
 * NodeDateRangeInput
 *
 * Shared date range input for schema-driven range fields.
 *
 * @param {object} value - Current field value.
 * @param {Function} onChange - Callback when range value changes.
 * @param {string} className - Optional wrapper class.
 * @param {boolean} hybrid - Whether to use hybrid DatePicker mode.
 * @param {boolean} autoFillEnd - Whether to auto-fill end date when needed.
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeDateRangeInput = ({
  value,
  onChange,
  hybrid = true,
  autoFillEnd = true,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startInvalid, setStartInvalid] = useState(false);
  const [endInvalid, setEndInvalid] = useState(false);

  // ----- REFS ----- //

  const startRawRef = useRef('');
  const endRawRef = useRef('');

  // ----- UTILITIES ----- //

  /**
   * Builds the value object to be emitted on change, based on the provided start and end dates.
   * If no start date is provided, the range is considered cleared and null values are emitted.
   *
   * @param {Date|null} startDate - The selected start date.
   * @param {Date|null} endDate - The selected end date.
   * 
   * @returns {object|null} The normalized range value object or null if cleared.
   */

  const buildRangeValue = (startDate, endDate) => {

    // If no start date, consider the range cleared (end date is irrelevant without a start).
    if (!startDate) return clearDates();

    return {
      startDate: timestampFromDate(startDate),
      startPrecision: 'date',
      endDate: endDate ? timestampFromDate(endDate) : null,
      endPrecision: endDate ? 'date' : null,
      isRange: Boolean(endDate),
    };
  };

  /**
   * Emits the change event with the normalized range value based on the provided start and end dates.
   *
   * @param {Date|null} nextStartDate - The next start date.
   * @param {Date|null} nextEndDate - The next end date.
   */
  const emitChange = (nextStartDate, nextEndDate) => {
    if (typeof onChange !== 'function') return;
    onChange(buildRangeValue(nextStartDate, nextEndDate));
  };

  // ----- EFFECTS ----- //

  // When the input value changes (e.g. from external updates), normalize and set local state.

  useEffect(() => {
    setStartDate(normalizeDateValue(value?.startDate));
    setEndDate(normalizeDateValue(value?.endDate));
  }, [value]);

  // ----- HANDLERS ----- //

  /**
   * Handles raw input changes for the start date, updating the corresponding ref and 
   * validation state.
   */

  const handleStartRawChange = (rawValue) => {
    startRawRef.current = rawValue || '';
    if (!isCompleteRawDate(rawValue)) {
      setStartInvalid(false);
    }
  };

  /**
   * Handles raw input changes for the end date, updating the corresponding ref and 
   * validation state.
   */
  const handleEndRawChange = (rawValue) => {
    endRawRef.current = rawValue || '';
    if (!isCompleteRawDate(rawValue)) {
      setEndInvalid(false);
    }
  };

  /**
   * Handles changes to the start date, applying auto-fill logic for the end date if enabled,
   * and emitting the normalized range value.
    *
    * @param {Date|null} nextStartDate - The next start date selected by the user.
   */
  const handleStartChange = (nextStartDate) => {
    setStartDate(nextStartDate);

    if (nextStartDate) {
      setStartInvalid(false);

      let nextEndDate = endDate;
      if (autoFillEnd && !nextEndDate) {
        nextEndDate = addDays(nextStartDate, 1);
      } else if (autoFillEnd && nextEndDate && nextEndDate < nextStartDate) {
        nextEndDate = addDays(nextStartDate, 1);
      }

      setEndDate(nextEndDate || null);
      emitChange(nextStartDate, nextEndDate || null);
      return;
    }

    const rawValue = startRawRef.current || '';
    if (!rawValue) {
      setStartInvalid(false);
      setEndDate(null);
      setEndInvalid(false);
      emitChange(null, null);
      return;
    }

    if (isCompleteRawDate(rawValue)) {
      setStartInvalid(true);
    }
  };

  /**
   * Handles changes to the end date, applying auto-fill logic if enabled, and emitting the
   * normalized range value. If the end date is cleared but has a raw value, it is considered
   * invalid until the raw value is cleared or a valid date is selected.
   *
   * @param {Date|null} nextEndDate - The next end date selected by the user.
   */
  /**
 * Handles end change.
 *
 * @param {*} nextEndDate
 */
  const handleEndChange = (nextEndDate) => {

    // If no start date, do not allow setting an end date (end date is irrelevant without a start).
    if (!startDate) return;

    // Update end date state and validation.
    setEndDate(nextEndDate);

    // If an end date is selected, ensure it's not before the start date and clear any 
    // validation error.
    if (nextEndDate) {
      setEndInvalid(false);
      const normalizedEndDate = nextEndDate < startDate ? addDays(startDate, 1) : nextEndDate;
      setEndDate(normalizedEndDate);
      emitChange(startDate, normalizedEndDate);
      return;
    }

    // If end date is cleared but has a raw value, consider it invalid until resolved.
    const rawValue = endRawRef.current || '';
    if (!rawValue) {
      setEndInvalid(false);
      if (autoFillEnd) {
        const fallbackEndDate = addDays(startDate, 1);
        setEndDate(fallbackEndDate);
        emitChange(startDate, fallbackEndDate);
      } else {
        emitChange(startDate, null);
      }
      return;
    }

    // If the raw value is a complete date, mark it as invalid since it doesn't match the 
    // cleared state.
    if (isCompleteRawDate(rawValue)) {
      setEndInvalid(true);
    }
  };

  // ----- MEMOS ----- //

  // Compute input class names based on validation state to apply error styling.

  const startInputClassName = useMemo(() => (
    startInvalid ? 'date-picker-invalid' : ''
  ), [startInvalid]);

  // Compute input class names based on validation state to apply error styling.
  const endInputClassName = useMemo(() => (
    endInvalid ? 'date-picker-invalid' : ''
  ), [endInvalid]);

  // ----- RENDER ----- //

  return (

    <div className="node-date-range-input-container">

      {/* ROW */}
      <div className="node-date-range-input-row">

        {/* LABEL */}
        <div
          className="node-date-range-input-label"
        >Start</div>

        {/* PICKER */}
        <DatePicker
          className="node-date-range-picker"
          value={startDate}
          inputClassName={startInputClassName}
          startIconKey="calendar_month"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid={hybrid}
          onChange={handleStartChange}
          onRawChange={handleStartRawChange}
        />
      </div>

      {/* ROW */}
      <div className="node-date-range-input-row">

        {/* LABEL */}
        <div
          className="node-date-range-input-label"
        >End</div>

        {/* PICKER */}
        <DatePicker
          className="node-date-range-picker"
          value={endDate}
          inputClassName={endInputClassName}
          startIconKey="calendar_month"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid={hybrid}
          disabled={!startDate}
          onChange={handleEndChange}
          onRawChange={handleEndRawChange}
        />

      </div>

    </div>

  );
};

// Dev Tools
NodeDateRangeInput.displayName = 'NodeDateRangeInput';

export default NodeDateRangeInput;
